import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { BURGER_MENU_PANEL_ID } from "web/react/components/navigation/navigation.constants";
import { useNavigationContext } from "web/react/components/navigation/navigation.context";
import { usePrevious } from "web/react/hooks/use-previous/use-previous";
import withReduxProvider from "web/react/redux-provider";
import { closeSearch } from "web/redux/ducks/app-header-bar";
import { triggerBanner } from "web/redux/ducks/follow-designer-banner";
import { MainNavigation } from "web/redux/ducks/main-navigation";
import { ReduxStoreState } from "web/redux/store";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./main-navigation-toggle-mobile.module.css";

const MENU_OPENED = "opened";
const MENU_CLOSED = "closed";

function extractNavigation(state: ReduxStoreState): MainNavigation {
    return state.mainNavigationReducer;
}

function selectIsBannerVisible(state: ReduxStoreState): boolean {
    return state.followDesignerBannerReducer.show;
}

export function MainNavigationToggleMobile(): React.ReactElement {
    const [shouldShowBanner, setShouldShowBanner] = useState(false);
    const { selectedGender } = useReduxSelector(extractNavigation);
    const bannerVisible = useReduxSelector(selectIsBannerVisible);

    const dispatch = useDispatch();

    const { showMenu, setShowMenu, showSearch, setShowSearch, handleMenuOpenedAnalytics } =
        useNavigationContext();
    const isMenuOpen = showMenu === "mobile";

    const buttonRef = useRef<HTMLButtonElement>(null);
    const wasMenuOpen = usePrevious(isMenuOpen);

    useEffect(() => {
        if (wasMenuOpen && !isMenuOpen) {
            buttonRef.current?.focus({ preventScroll: true });
        }
    }, [isMenuOpen, wasMenuOpen]);

    function sendAnalytics(menuVisibility): void {
        const gender = selectedGender || "women";
        handleMenuOpenedAnalytics(menuVisibility, gender);
    }

    function toggleVisibility(): void {
        if (showMenu) {
            if (shouldShowBanner && !bannerVisible) {
                dispatch(triggerBanner(true));
            }
            setShowMenu(null);
            sendAnalytics(MENU_CLOSED);
        } else {
            if (bannerVisible) {
                setShouldShowBanner(true);
                dispatch(triggerBanner(false));
            }
            setShowMenu("mobile");
            setShowSearch(null);
            dispatch(closeSearch());
            sendAnalytics(MENU_OPENED);
        }
    }

    return (
        <button
            ref={buttonRef}
            data-testid="navigation-toggle-mobile"
            type="button"
            aria-controls={BURGER_MENU_PANEL_ID}
            aria-label={
                isMenuOpen
                    ? gettext("header.navigation.close_menu")
                    : gettext("header.navigation.open_menu")
            }
            aria-expanded={isMenuOpen}
            className={clsx(isMenuOpen ? styles.burgerCross : styles.burgerContainer, {
                [styles.iconGrey]: showSearch,
            })}
            onClick={toggleVisibility}
        >
            <div className={styles.burgerBar1} />
            <div className={styles.burgerBar2} />
            <div className={styles.burgerBar3} />
        </button>
    );
}

export default withReduxProvider(MainNavigationToggleMobile);
